import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalCard, BrutalInput } from "./Brutal";
import { copyToClipboard } from "../lib/clipboard";
import { UsersThree, Copy, Trash, PaperPlaneTilt, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

/** v414.46 — shared store access. Extra sign-ins (own email + own password)
 * that act as this distributor account: same store code, same inventory,
 * same payouts. Temp passwords are shown once, exactly like resend-welcome. */
export function CoLoginsCard({ distributorId, distributorName }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null); // {name, email, password, email_sent}

  const load = () =>
    api.get(`/distributors/${distributorId}/co-logins`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [distributorId]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/distributors/${distributorId}/co-logins`, {
        name: name.trim(), email: email.trim(),
      });
      setCreds({ name: data.name, email: data.email, password: data.temp_password, email_sent: data.email_sent });
      setName(""); setEmail(""); setAdding(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (row) => {
    try {
      const { data } = await api.post(`/distributors/${distributorId}/co-logins/${row.id}/resend-welcome`);
      setCreds({ name: data.name, email: data.sent_to, password: data.temp_password, email_sent: data.email_sent });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(t("co_logins.remove_confirm", { email: row.email }))) return;
    try {
      await api.delete(`/distributors/${distributorId}/co-logins/${row.id}`);
      toast.success(t("co_logins.removed"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <BrutalCard data-testid="co-logins-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <UsersThree size={18} weight="bold" className="text-[#8a6a1c]" />
            {t("co_logins.title")}
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">{t("co_logins.hint", { name: distributorName })}</p>
        </div>
        {!adding && (
          <BrutalButton size="sm" variant="secondary" onClick={() => setAdding(true)} data-testid="co-login-add-btn">
            <Plus size={14} weight="bold" /> {t("co_logins.add_btn")}
          </BrutalButton>
        )}
      </div>

      {adding && (
        <form onSubmit={add} className="mt-3 flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-semibold mb-1">{t("common.name")}</label>
            <BrutalInput value={name} onChange={(e) => setName(e.target.value)} required minLength={2} data-testid="co-login-name-input" />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-semibold mb-1">{t("common.email")}</label>
            <BrutalInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="co-login-email-input" />
          </div>
          <BrutalButton type="submit" size="sm" disabled={busy} data-testid="co-login-create-btn">
            {busy ? t("common.loading") : t("co_logins.create_btn")}
          </BrutalButton>
          <BrutalButton type="button" size="sm" variant="secondary" onClick={() => setAdding(false)} data-testid="co-login-cancel-btn">
            {t("common.cancel")}
          </BrutalButton>
        </form>
      )}

      {items.length > 0 && (
        <div className="mt-3 space-y-2">
          {items.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 border border-zinc-200 rounded-lg px-3 py-2" data-testid={`co-login-row-${r.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{r.name}</p>
                <p className="text-xs text-zinc-500 truncate">{r.email}
                  {r.last_login_at
                    ? ` · ${t("co_logins.last_login")} ${new Date(r.last_login_at).toLocaleDateString()}`
                    : ` · ${t("co_logins.never_logged_in")}`}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => resend(r)} title={t("co_logins.resend_title")}
                  className="p-1.5 text-zinc-500 hover:text-[#8a6a1c]" data-testid={`co-login-resend-${r.id}`}>
                  <PaperPlaneTilt size={16} weight="bold" />
                </button>
                <button onClick={() => remove(r)} title={t("co_logins.remove_title")}
                  className="p-1.5 text-zinc-500 hover:text-red-600" data-testid={`co-login-remove-${r.id}`}>
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {items.length === 0 && !adding && (
        <p className="text-sm text-zinc-500 mt-2" data-testid="co-logins-empty">{t("co_logins.empty")}</p>
      )}

      {creds && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="co-login-creds-dialog">
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-xl w-full max-w-sm p-5">
            <h2 className="text-lg font-semibold mb-1">{t("co_logins.creds_title")}</h2>
            <p className="text-sm text-zinc-600 mb-3">
              {t("co_logins.creds_subtitle", { name: creds.name || creds.email })}
              {!creds.email_sent && <span className="text-amber-700"> {t("co_logins.email_failed_note")}</span>}
            </p>
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 font-mono text-sm space-y-1" data-testid="co-login-creds-value">
              <div><strong>{t("common.email")}:</strong> {creds.email}</div>
              <div><strong>{t("common.password")}:</strong> {creds.password}</div>
            </div>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
              {t("distributors.temp_pw_once_note")}
            </p>
            <BrutalButton className="w-full mt-3"
              onClick={() => copyToClipboard(
                `Email: ${creds.email}\nPassword: ${creds.password}\nLogin: ${window.location.origin}/login`,
                t("common.copied"))}
              data-testid="co-login-creds-copy">
              <Copy size={14} weight="bold" /> {t("distributors.copy_creds")}
            </BrutalButton>
            <BrutalButton variant="secondary" className="w-full mt-2" onClick={() => setCreds(null)} data-testid="co-login-creds-done">
              {t("common.done")}
            </BrutalButton>
          </div>
        </div>
      )}
    </BrutalCard>
  );
}
